import React, { useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  InputProps,
  TextareaProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  useColorModeValue,
} from "@chakra-ui/react";

/** Matches `ConfiguratorStyleMenu` trigger height. */
export const CONFIGURATOR_FIELD_CONTROL_HEIGHT = "32px";

/** Dark-mode surfaces aligned with sidebar / card tokens. */
const CONFIGURATOR_DARK_SURFACE = {
  bg: "var(--sidebar)",
  border: "var(--border)",
  text: "var(--sidebar-muted-foreground)",
  hoverBg: "var(--muted)",
  menuHoverBg: "var(--sidebar-accent)",
  chevron: "var(--sidebar-muted-foreground)",
} as const;

/** Text / number inputs aligned with configurator menu triggers. */
export function useConfiguratorStyleInputProps(): InputProps {
  const bg = useColorModeValue("white", CONFIGURATOR_DARK_SURFACE.bg);
  const borderColor = useColorModeValue("gray.200", CONFIGURATOR_DARK_SURFACE.border);
  const color = useColorModeValue("gray.600", CONFIGURATOR_DARK_SURFACE.text);
  const hoverBg = useColorModeValue("gray.50", CONFIGURATOR_DARK_SURFACE.hoverBg);
  const placeholderColor = useColorModeValue("gray.400", CONFIGURATOR_DARK_SURFACE.text);

  return {
    size: "sm",
    h: CONFIGURATOR_FIELD_CONTROL_HEIGHT,
    minH: CONFIGURATOR_FIELD_CONTROL_HEIGHT,
    px: "12px",
    py: "8px",
    fontSize: "xs",
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontWeight: "medium",
    bg,
    color,
    border: "1px solid",
    borderColor,
    borderRadius: "md",
    _placeholder: { color: placeholderColor, fontWeight: "normal" },
    _hover: { bg: hoverBg },
    _focus: { bg, borderColor: "gray.500", boxShadow: "none" },
    _focusVisible: { borderColor: "gray.500", boxShadow: "none" },
  };
}

/** Multiline fields — same chrome as `useConfiguratorStyleInputProps` without fixed single-line height. */
export function useConfiguratorStyleTextareaProps(): TextareaProps {
  const bg = useColorModeValue("white", CONFIGURATOR_DARK_SURFACE.bg);
  const borderColor = useColorModeValue("gray.200", CONFIGURATOR_DARK_SURFACE.border);
  const color = useColorModeValue("gray.600", CONFIGURATOR_DARK_SURFACE.text);
  const hoverBg = useColorModeValue("gray.50", CONFIGURATOR_DARK_SURFACE.hoverBg);
  const placeholderColor = useColorModeValue("gray.400", CONFIGURATOR_DARK_SURFACE.text);

  return {
    size: "sm",
    px: "12px",
    py: "10px",
    fontSize: "xs",
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontWeight: "medium",
    bg,
    color,
    border: "1px solid",
    borderColor,
    borderRadius: "md",
    _placeholder: { color: placeholderColor, fontWeight: "normal" },
    _hover: { bg: hoverBg },
    _focus: { bg, borderColor: "gray.500", boxShadow: "none" },
    _focusVisible: { borderColor: "gray.500", boxShadow: "none" },
  };
}

const svgStrokeProps = { strokeWidth: 1.5 as const, stroke: "currentColor" as const };

/** Heroicons outline chevron-down (stroke 1.5). */
export function ConfiguratorChevronDownIcon(
  props: Omit<React.SVGProps<SVGSVGElement>, "xmlns" | "fill" | "viewBox" | "stroke" | "strokeWidth">,
): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden {...props} {...svgStrokeProps}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

/** Heroicons outline chevron-up (stroke 1.5). */
export function ConfiguratorChevronUpIcon(
  props: Omit<React.SVGProps<SVGSVGElement>, "xmlns" | "fill" | "viewBox" | "stroke" | "strokeWidth">,
): React.ReactElement {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden {...props} {...svgStrokeProps}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  );
}

export type ConfiguratorStyleMenuOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type ConfiguratorStyleMenuProps = {
  currentLabel: string;
  options: ConfiguratorStyleMenuOption[];
  onSelect: (value: string) => void;
  isDisabled?: boolean;
  /** Match slice configurator: menu list matches trigger width. */
  matchWidth?: boolean;
  maxW?: string;
  w?: string;
  /** Minimum trigger width (e.g. long placeholder + chevron). */
  minW?: string | number;
  /**
   * Slice configurator uses uppercase labels. Set false for long mixed-case labels
   * (e.g. policy titles, simulation names).
   */
  uppercaseLabels?: boolean;
  /**
   * When set, overrides `uppercaseLabels` for trigger and menu item text transform.
   */
  labelTextTransform?: "uppercase" | "capitalize" | "none";
  /**
   * Muted label + chevron (#9CA3AF), aligned with findings / notifications chrome.
   */
  notificationsMutedChrome?: boolean;
  /**
   * full: trigger fills parent width (default).
   * hug: trigger width follows the longest option label (+ chevron/padding), capped for safety.
   */
  triggerWidth?: "full" | "hug";
  /** Trigger + menu list radius (default Chakra `md`). */
  borderRadius?: string;
};

function longestOptionLabelLength(currentLabel: string, options: ConfiguratorStyleMenuOption[]): number {
  const lens = [currentLabel, ...options.map((o) => o.label)].map((s) => (s != null ? String(s).length : 0));
  return Math.max(1, ...lens);
}

/** Approximate trigger min-width from label length (xs IBM Plex Sans + chevron + horizontal padding). */
function hugTriggerMinCh(currentLabel: string, options: ConfiguratorStyleMenuOption[]): string {
  const n = longestOptionLabelLength(currentLabel, options);
  const ch = Math.min(56, Math.max(8, n + 6));
  return `${ch}ch`;
}

/**
 * Custom dropdown matching Market Context “Slice configurator” menus (Menu + bordered trigger).
 */
export function ConfiguratorStyleMenu({
  currentLabel,
  options,
  onSelect,
  isDisabled,
  matchWidth = true,
  maxW,
  w = "100%",
  minW,
  uppercaseLabels = true,
  labelTextTransform,
  notificationsMutedChrome = false,
  triggerWidth = "full",
  borderRadius: menuBorderRadius = "md",
}: ConfiguratorStyleMenuProps): React.ReactElement {
  const configSelectBg = useColorModeValue("white", CONFIGURATOR_DARK_SURFACE.bg);
  const configSelectBorderColor = useColorModeValue("gray.200", CONFIGURATOR_DARK_SURFACE.border);
  const defaultSelectTextColor = useColorModeValue("gray.600", CONFIGURATOR_DARK_SURFACE.text);
  const configSelectTextColor = notificationsMutedChrome ? "#9CA3AF" : defaultSelectTextColor;
  const configSelectHoverBg = useColorModeValue("gray.50", CONFIGURATOR_DARK_SURFACE.hoverBg);
  const configMenuBg = useColorModeValue("white", CONFIGURATOR_DARK_SURFACE.bg);
  const configMenuHoverBg = useColorModeValue("gray.100", CONFIGURATOR_DARK_SURFACE.menuHoverBg);
  const defaultChevronColor = useColorModeValue("#6B7280", CONFIGURATOR_DARK_SURFACE.chevron);
  const chevronIconColor = notificationsMutedChrome ? "#9CA3AF" : defaultChevronColor;

  const textTransform =
    labelTextTransform ?? (uppercaseLabels ? "uppercase" : "none");

  const labelLetterSpacing = textTransform === "uppercase" ? "0.02em" : undefined;
  const labelLineHeight = textTransform === "uppercase" ? "1" : undefined;

  const hugMinW = useMemo(() => hugTriggerMinCh(currentLabel, options), [currentLabel, options]);
  const isHug = triggerWidth === "hug";
  const resolvedW = isHug ? "auto" : w;
  const resolvedMinW = isHug ? hugMinW : minW !== undefined && minW !== null ? minW : 0;
  const resolvedMaxW = maxW ?? (isHug ? undefined : "100%");
  return (
    <Box w={isHug ? undefined : "100%"} minW={0} maxW={resolvedMaxW}>
    <Menu matchWidth={matchWidth}>
      <MenuButton
        as={Button}
        display="flex"
        alignItems="center"
        size="sm"
        bg={configSelectBg}
        w={resolvedW}
        minW={resolvedMinW}
        maxW={resolvedMaxW}
        h="32px"
        overflow="hidden"
        px="12px"
        py="8px"
        fontSize="xs"
        fontFamily="'IBM Plex Sans', sans-serif"
        fontWeight="medium"
        color={configSelectTextColor}
        textTransform={textTransform}
        letterSpacing={labelLetterSpacing}
        lineHeight={labelLineHeight}
        border="1px solid"
        borderColor={configSelectBorderColor}
        borderRadius={menuBorderRadius}
        justifyContent="space-between"
        gap={2}
        _hover={{ bg: configSelectHoverBg }}
        _active={{ bg: configSelectBg }}
        _focus={{ bg: configSelectBg, boxShadow: "none" }}
        _focusVisible={{ boxShadow: "none" }}
        isDisabled={isDisabled}
        title={currentLabel}
      >
        <Flex align="center" gap={2} minW={0} w="100%" overflow="hidden">
          <Box
            as="span"
            display="block"
            flex="1 1 0%"
            minW={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            textAlign="left"
          >
            {currentLabel}
          </Box>
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            color={chevronIconColor}
            aria-hidden
          >
            <ConfiguratorChevronDownIcon width={16} height={16} />
          </Box>
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList
          bg={configMenuBg}
          borderColor={configSelectBorderColor}
          borderRadius={menuBorderRadius}
          maxH="280px"
          overflowY="auto"
          zIndex={1600}
        >
          {options.map((opt, index) => (
            <MenuItem
              key={`${opt.value || "empty"}-${index}`}
              onClick={() => onSelect(opt.value)}
              isDisabled={Boolean(opt.disabled)}
              fontSize="xs"
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              color={configSelectTextColor}
              textTransform={textTransform}
              letterSpacing={labelLetterSpacing}
              lineHeight={labelLineHeight}
              bg={configMenuBg}
              _hover={{ bg: configMenuHoverBg }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
    </Box>
  );
}

export type ConfiguratorStyleMultiSelectMenuProps = {
  selectedValues: string[];
  options: ConfiguratorStyleMenuOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  matchWidth?: boolean;
  maxW?: string;
  w?: string;
  minW?: string | number;
  uppercaseLabels?: boolean;
  labelTextTransform?: "uppercase" | "capitalize" | "none";
  notificationsMutedChrome?: boolean;
  triggerWidth?: "full" | "hug";
  borderRadius?: string;
};

function multiSelectCurrentLabel(
  selectedValues: string[],
  options: ConfiguratorStyleMenuOption[],
  placeholder: string,
): string {
  if (selectedValues.length === 0) return placeholder;
  const labelByValue = new Map(options.map((o) => [o.value, o.label]));
  return selectedValues.map((v) => labelByValue.get(v) ?? v).join(", ");
}

/**
 * Configurator-style dropdown with checkbox multi-select (menu stays open while selecting).
 */
export function ConfiguratorStyleMultiSelectMenu({
  selectedValues,
  options,
  onChange,
  placeholder = "Select...",
  isDisabled,
  matchWidth = true,
  maxW,
  w = "100%",
  minW,
  uppercaseLabels = true,
  labelTextTransform,
  notificationsMutedChrome = false,
  triggerWidth = "full",
  borderRadius: menuBorderRadius = "md",
}: ConfiguratorStyleMultiSelectMenuProps): React.ReactElement {
  const configSelectBg = useColorModeValue("white", CONFIGURATOR_DARK_SURFACE.bg);
  const configSelectBorderColor = useColorModeValue("gray.200", CONFIGURATOR_DARK_SURFACE.border);
  const defaultSelectTextColor = useColorModeValue("gray.600", CONFIGURATOR_DARK_SURFACE.text);
  const configSelectTextColor = notificationsMutedChrome ? "#9CA3AF" : defaultSelectTextColor;
  const configSelectHoverBg = useColorModeValue("gray.50", CONFIGURATOR_DARK_SURFACE.hoverBg);
  const configMenuBg = useColorModeValue("white", CONFIGURATOR_DARK_SURFACE.bg);
  const configMenuHoverBg = useColorModeValue("gray.100", CONFIGURATOR_DARK_SURFACE.menuHoverBg);
  const defaultChevronColor = useColorModeValue("#6B7280", CONFIGURATOR_DARK_SURFACE.chevron);
  const chevronIconColor = notificationsMutedChrome ? "#9CA3AF" : defaultChevronColor;

  const textTransform =
    labelTextTransform ?? (uppercaseLabels ? "uppercase" : "none");

  const labelLetterSpacing = textTransform === "uppercase" ? "0.02em" : undefined;
  const labelLineHeight = textTransform === "uppercase" ? "1" : undefined;

  const currentLabel = useMemo(
    () => multiSelectCurrentLabel(selectedValues, options, placeholder),
    [selectedValues, options, placeholder],
  );

  const hugMinW = useMemo(() => hugTriggerMinCh(currentLabel, options), [currentLabel, options]);
  const isHug = triggerWidth === "hug";
  const resolvedW = isHug ? "auto" : w;
  const resolvedMinW = isHug ? hugMinW : minW !== undefined && minW !== null ? minW : 0;
  const resolvedMaxW = maxW ?? (isHug ? undefined : "100%");

  return (
    <Box w={isHug ? undefined : "100%"} minW={0} maxW={resolvedMaxW}>
    <Menu matchWidth={matchWidth} closeOnSelect={false}>
      <MenuButton
        as={Button}
        display="flex"
        alignItems="center"
        size="sm"
        bg={configSelectBg}
        w={resolvedW}
        minW={resolvedMinW}
        maxW={resolvedMaxW}
        h="32px"
        overflow="hidden"
        px="12px"
        py="8px"
        fontSize="xs"
        fontFamily="'IBM Plex Sans', sans-serif"
        fontWeight="medium"
        color={configSelectTextColor}
        textTransform={textTransform}
        letterSpacing={labelLetterSpacing}
        lineHeight={labelLineHeight}
        border="1px solid"
        borderColor={configSelectBorderColor}
        borderRadius={menuBorderRadius}
        justifyContent="space-between"
        gap={2}
        _hover={{ bg: configSelectHoverBg }}
        _active={{ bg: configSelectBg }}
        _focus={{ bg: configSelectBg, boxShadow: "none" }}
        _focusVisible={{ boxShadow: "none" }}
        isDisabled={isDisabled}
        title={currentLabel}
      >
        <Flex align="center" gap={2} minW={0} w="100%" overflow="hidden">
          <Box
            as="span"
            display="block"
            flex="1 1 0%"
            minW={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            textAlign="left"
          >
            {currentLabel}
          </Box>
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            color={chevronIconColor}
            aria-hidden
          >
            <ConfiguratorChevronDownIcon width={16} height={16} />
          </Box>
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList
          bg={configMenuBg}
          borderColor={configSelectBorderColor}
          borderRadius={menuBorderRadius}
          maxH="280px"
          overflowY="auto"
          zIndex={1600}
        >
          <MenuOptionGroup
            type="checkbox"
            value={selectedValues}
            onChange={(val) => {
              const next = Array.isArray(val) ? val : val != null && val !== "" ? [String(val)] : [];
              onChange(next);
            }}
          >
            {options.map((opt, index) => (
              <MenuItemOption
                key={`${opt.value || "empty"}-${index}`}
                value={opt.value}
                isDisabled={Boolean(opt.disabled)}
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                color={configSelectTextColor}
                textTransform={textTransform}
                letterSpacing={labelLetterSpacing}
                lineHeight={labelLineHeight}
                bg={configMenuBg}
                _hover={{ bg: configMenuHoverBg }}
              >
                {opt.label}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Portal>
    </Menu>
    </Box>
  );
}
